import React from 'react';
import { Link } from 'react-router-dom';
import FlowRCMLogo from '../components/FlowRCMLogo';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200">
        <div className="flex justify-center py-6">
          <Link to="/login" className="inline-block">
            <FlowRCMLogo variant="light" hideSubtitle={true} />
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-600 mb-8">Last Updated: January 8, 2026</p>

          <div className="space-y-6 text-slate-700">
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Introduction</h2>
              <p>
                FlowRCM ("we," "our," or "us") provides HIPAA-compliant revenue cycle management services 
                for dental practices. This Privacy Policy explains how we collect, use, disclose, and 
                safeguard Protected Health Information (PHI) and other data when you use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Information We Collect</h2>
              
              <h3 className="font-semibold text-slate-900 mt-4 mb-2">Practice Account Information</h3>
              <p className="mb-2">We collect information about your dental practice:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Practice name and contact information</li>
                <li>Staff member names and email addresses</li>
                <li>National Provider Identifier (NPI) numbers</li>
                <li>Billing information (processed securely by third-party payment processors)</li>
              </ul>

              <h3 className="font-semibold text-slate-900 mt-4 mb-2">Protected Health Information (PHI)</h3>
              <p className="mb-2">
                As a HIPAA-compliant service, we process PHI to provide revenue cycle management services. 
                This includes:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Patient demographics (name, date of birth, contact information)</li>
                <li>Insurance information and eligibility data</li>
                <li>Treatment records and procedure codes (CDT codes)</li>
                <li>Claims and billing information</li>
                <li>Medical and dental history relevant to claims processing</li>
              </ul>
              <p className="mt-2">
                We process this information solely for treatment, payment, and healthcare operations as 
                permitted under HIPAA regulations.
              </p>

              <h3 className="font-semibold text-slate-900 mt-4 mb-2">Usage and Technical Data</h3>
              <p className="mb-2">We automatically collect information about system usage:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Login times and session duration</li>
                <li>Workflow executions and features used</li>
                <li>IP addresses and device information (for security purposes)</li>
                <li>Browser type and operating system</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">3. How We Use Your Information</h2>
              <p className="mb-2">We use collected information to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Verify insurance eligibility and benefits</li>
                <li>Process pre-authorizations and submit claims</li>
                <li>Validate claim accuracy and identify potential denials</li>
                <li>Manage denial appeals and revenue recovery</li>
                <li>Provide customer support for your practice</li>
                <li>Improve our AI algorithms and service quality</li>
                <li>Comply with legal obligations and HIPAA requirements</li>
                <li>Detect and prevent fraud or security breaches</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">4. HIPAA Compliance & Business Associate Agreement</h2>
              <p>
                FlowRCM operates as a HIPAA-covered Business Associate. We maintain comprehensive safeguards 
                to protect all PHI:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Administrative Safeguards:</strong> Written policies, workforce training, designated Privacy and Security Officers, and strict access controls</li>
                <li><strong>Physical Safeguards:</strong> Secure data centers with restricted physical access, facility security plans, and workstation security protocols</li>
                <li><strong>Technical Safeguards:</strong> End-to-end encryption (TLS 1.3 in transit, AES-256 at rest), automatic session timeouts after 15 minutes of inactivity, comprehensive audit logging, and regular security assessments</li>
              </ul>
              <p className="mt-3">
                We execute a Business Associate Agreement (BAA) with all dental practices using our production 
                service, as required by HIPAA. This agreement outlines our responsibilities for safeguarding 
                PHI and our obligations in the event of a breach.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Information Sharing & Disclosure</h2>
              <p className="mb-2">
                We do not sell PHI or practice data. We may share information only in these limited circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Healthcare Operations:</strong> With insurance payers and clearinghouses to process claims and verify eligibility (as permitted under HIPAA)</li>
                <li><strong>Service Providers:</strong> With HIPAA-compliant subcontractors who assist in providing the Service (e.g., secure cloud hosting), all of whom sign Business Associate Agreements</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or to comply with legal processes</li>
                <li><strong>Breach Notification:</strong> With affected practices and the HHS Office for Civil Rights in the event of a PHI breach, as required by HIPAA</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with continued HIPAA protections)</li>
                <li><strong>Your Authorization:</strong> With your explicit written permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Data Security Measures</h2>
              <p>
                We implement industry-leading security measures specifically designed for healthcare data:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>TLS 1.3 encryption for all data in transit</li>
                <li>AES-256 encryption for data at rest</li>
                <li>Multi-factor authentication (MFA) for all user accounts</li>
                <li>Role-based access controls (RBAC) limiting data access to authorized personnel</li>
                <li>Automatic session timeouts after 15 minutes of inactivity</li>
                <li>Comprehensive audit logging of all PHI access and modifications</li>
                <li>Regular vulnerability assessments and penetration testing</li>
                <li>Intrusion detection and prevention systems</li>
                <li>Regular security awareness training for all staff</li>
                <li>Incident response plan and breach notification procedures</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Data Retention</h2>
              <p>
                We retain PHI and practice data according to HIPAA requirements and industry standards:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Active Accounts:</strong> Data is retained for as long as your practice account is active</li>
                <li><strong>PHI Retention:</strong> Minimum of 6 years from the date of creation or last use, as required by HIPAA</li>
                <li><strong>Audit Logs:</strong> Retained for 6 years to meet HIPAA compliance requirements</li>
                <li><strong>Account Termination:</strong> Upon termination, you may request data export within 30 days. After 30 days, data is securely deleted according to NIST standards</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Your Rights Under HIPAA</h2>
              <p>
                As a dental practice using our service, you and your patients have rights under HIPAA. 
                For information about your rights, please contact our Privacy Officer at info@flowrcmdental.com.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Cookies and Session Management</h2>
              <p>
                We use essential session cookies to maintain your login state and secure your session. 
                We do not use tracking cookies, advertising cookies, or third-party analytics that could 
                compromise PHI security. Session cookies expire after logout or 15 minutes of inactivity.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy to reflect changes in our practices or legal requirements. 
                We will notify you of material changes via email at least 30 days before they take effect. 
                Continued use of the Service after changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Contact Information</h2>
              <p>
                For questions about this Privacy Policy, to exercise your privacy rights, or to report a privacy or security concern:<br /><br />
                Email: support@flowrcmdental.com<br />
                Privacy Officer: FlowRCM Privacy Team
              </p>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <Link to="/login" className="text-teal-600 hover:text-teal-700 font-semibold">
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;